import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { ChartDataService } from "../../services/ChartDataService";
import type { Machine } from "../../services/BackendDataService";
import { AvailableDatePicker } from "../AvailableDatePicker";
import {
  ShiftAnalysisService,
  type ShiftAnalysis as ShiftAnalysisData,
} from "../../services/ShiftAnalysisService";
import ShiftVerdictHeader from "./ShiftVerdictHeader";
import ShiftScoreGauge from "./ShiftScoreGauge";
import MachinePolar from "./MachinePolar";
import LinePulse from "./LinePulse";
import DowntimeSankey from "./DowntimeSankey";
import DowntimeTreemap from "./DowntimeTreemap";
import StatusTimeline from "./StatusTimeline";
import StateBreakdown from "./StateBreakdown";
import BlameFlow from "./BlameFlow";
import DowntimeSplit from "./DowntimeSplit";
import LostProduction from "./LostProduction";
import SpeedLosses from "./SpeedLosses";
import KpiTimeline from "./KpiTimeline";
import Panel from "./Panel";

const CARD_STYLE: React.CSSProperties = {
  backgroundColor: "var(--bg-primary)",
  borderColor: "var(--border-primary)",
  boxShadow: "0 1px 2px rgba(16,24,40,0.06), 0 4px 12px rgba(16,24,40,0.05)",
};

const MACHINE_STATUS_LABEL: Record<string, string> = {
  good: "Good",
  watch: "Watch",
  bad: "Bad",
  no_data: "No data",
};

/** Compact "1h 20m" / "45m" downtime label. */
function fmtDowntime(ms: number): string {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

const MACHINE_STATUS_COLOR: Record<string, string> = {
  good: "#22c55e",
  watch: "#eab308",
  bad: "#ef4444",
  no_data: "#9ca3af",
};

interface Props {
  lineId: string;
  lineName?: string;
  factory?: string;
  /** All machines on the line — used to resolve which dates have data. */
  machines?: Machine[];
}

const Spinner = ({ label }: { label: string }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-600" />
    <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
      {label}
    </span>
  </div>
);

const ShiftAnalysis: React.FC<Props> = ({
  lineId,
  lineName,
  machines = [],
}) => {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();

  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [date, setDate] = useState<string>("");
  const [availableShifts, setAvailableShifts] = useState<string[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shift, setShift] = useState<string>("");

  const [data, setData] = useState<ShiftAnalysisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Focus model: hovering any element previews focus; clicking pins it.
  const [hoverSn, setHoverSn] = useState<string | null>(null);
  const [pinnedSn, setPinnedSn] = useState<string | null>(null);
  const focusedSn = hoverSn ?? pinnedSn;
  const handlePin = useCallback(
    (sn: string) => setPinnedSn((p) => (p === sn ? null : sn)),
    [],
  );

  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Serials of machines on the line that actually have data.
  const serials = useMemo(
    () =>
      machines
        .filter((m) => m.datenVerfuegbar)
        .map((m) => m.maschinennummer || m.maschineBK)
        .filter((s): s is string => !!s)
        .slice(0, 12),
    [machines],
  );

  // ── Available dates for the line (union across its machines) ────────────
  const loadDates = useCallback(async () => {
    const tok = tokenRef.current;
    if (!tok || serials.length === 0) {
      setAvailableDates([]);
      return;
    }
    setDatesLoading(true);
    try {
      const results = await Promise.all(
        serials.map((sn) =>
          ChartDataService.fetchAvailableDates({ serialno: sn }, tok).catch(
            () => null,
          ),
        ),
      );
      const set = new Set<string>();
      for (const res of results) {
        if (res?.success && res.data) {
          const rows = Array.isArray(res.data)
            ? res.data
            : (((res.data as { available_dates?: unknown }).available_dates as
                | { date?: string }[]
                | undefined) ?? []);
          for (const item of rows as { date?: string }[]) {
            if (item?.date) set.add(String(item.date).split("T")[0]);
          }
        }
      }
      setAvailableDates(Array.from(set).sort());
    } finally {
      setDatesLoading(false);
    }
  }, [serials]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  // Default to the newest available date (or keep a still-valid selection).
  useEffect(() => {
    if (availableDates.length === 0) return;
    setDate((prev) =>
      prev && availableDates.includes(prev)
        ? prev
        : dateRange.end && availableDates.includes(dateRange.end)
          ? dateRange.end
          : availableDates[availableDates.length - 1],
    );
  }, [availableDates, dateRange.end]);

  // ── Fetch available shifts for the chosen date ──────────────────────────
  const loadShifts = useCallback(async () => {
    const tok = tokenRef.current;
    if (!lineId || !date || !tok) return;
    setShiftsLoading(true);
    try {
      const res = await ChartDataService.fetchLineAvailableShifts(
        { line_bk: lineId, date_from: date, date_to: date },
        tok,
      );
      const rows = Array.isArray(res.data) ? res.data : [];
      const names = Array.from(
        new Set(
          rows
            .map((r: { shift_name?: string }) => r.shift_name)
            .filter((s): s is string => !!s),
        ),
      );
      setAvailableShifts(names);
      // Keep current selection if still valid, else pick the first.
      setShift((prev) =>
        prev && names.includes(prev) ? prev : (names[0] ?? ""),
      );
    } catch {
      setAvailableShifts([]);
      setShift("");
    } finally {
      setShiftsLoading(false);
    }
  }, [lineId, date]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  // ── Fetch the analysis when line + shift + date are all set ─────────────
  const loadAnalysis = useCallback(async () => {
    const tok = tokenRef.current;
    if (!lineId || !shift || !date || !tok) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setPinnedSn(null);
    setHoverSn(null);
    try {
      const result = await ShiftAnalysisService.getShiftAnalysis(
        lineId,
        shift,
        date,
        tok,
      );
      setData(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load shift analysis",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [lineId, shift, date]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const hasResult = !!data && !loading && !error;
  const isEmpty =
    hasResult &&
    data!.events.length === 0 &&
    data!.machines.length === 0 &&
    data!.stopCauses.length === 0 &&
    !data!.timelineSummary;

  return (
    <div
      className="p-4 sm:p-6 flex flex-col gap-4"
      style={{ minHeight: 400, backgroundColor: "var(--bg-secondary)" }}
    >
      {/* ── Picker bar ─────────────────────────────────────────────── */}
      <div
        className="rounded-2xl border p-3 sm:p-4 flex flex-wrap items-center gap-4"
        style={CARD_STYLE}
      >
        <div className="flex flex-col gap-1">
          <label
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("date", "Date")}
          </label>
          <div className="min-w-[170px]">
            {datesLoading && availableDates.length === 0 ? (
              <div
                className="rounded border px-3 py-2 text-sm"
                style={{
                  borderColor: "var(--border-primary)",
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-secondary)",
                }}
              >
                {t("loadingDates", "Loading dates…")}
              </div>
            ) : availableDates.length === 0 ? (
              <div
                className="rounded border px-3 py-2 text-sm italic"
                style={{
                  borderColor: "var(--border-primary)",
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-secondary)",
                }}
              >
                {t("noDatesWithData", "No dates with data")}
              </div>
            ) : (
              <AvailableDatePicker
                value={date}
                onChange={setDate}
                availableDates={availableDates}
                placeholder={t("selectDate", "Select date...")}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("shift", "Shift")}
          </label>
          <div className="flex items-center gap-1.5 min-h-[34px]">
            {shiftsLoading ? (
              <span
                className="text-xs"
                style={{ color: "var(--text-secondary)" }}
              >
                {t("loadingShifts", "Loading shifts…")}
              </span>
            ) : availableShifts.length === 0 ? (
              <span
                className="text-xs italic"
                style={{ color: "var(--text-secondary)" }}
              >
                {t("noShiftsOnDate", "No shifts on this date")}
              </span>
            ) : (
              availableShifts.map((s) => {
                const active = s === shift;
                return (
                  <button
                    key={s}
                    onClick={() => setShift(s)}
                    className="rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
                    style={{
                      backgroundColor: active
                        ? "#2563eb"
                        : "var(--bg-secondary)",
                      color: active ? "#fff" : "var(--text-secondary)",
                      border: `1px solid ${active ? "#2563eb" : "var(--border-primary)"}`,
                    }}
                  >
                    {s}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {data && (
          <div className="ml-auto text-right">
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {t("analysing", "Analysing")}
            </div>
            <div
              className="text-sm font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              {lineName || lineId} · {data.shiftName} · {data.shiftDate}
            </div>
          </div>
        )}
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Spinner label={t("analysingShift", "Analysing shift…")} />
          </motion.div>
        ) : error ? (
          <motion.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-16 gap-3 text-center"
          >
            <div className="text-5xl">⚠️</div>
            <h3
              className="text-lg font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              {t("shiftAnalysisError", "Could not load shift analysis")}
            </h3>
            <p
              className="text-sm max-w-md"
              style={{ color: "var(--text-secondary)" }}
            >
              {error}
            </p>
            <button
              onClick={loadAnalysis}
              className="mt-1 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              {t("retry", "Retry")}
            </button>
          </motion.div>
        ) : !shift ? (
          <motion.div
            key="noshift"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-16 gap-2 text-center"
          >
            <div className="text-5xl">📅</div>
            <h3
              className="text-lg font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              {t("pickShiftTitle", "Pick a date and shift")}
            </h3>
            <p
              className="text-sm max-w-md"
              style={{ color: "var(--text-secondary)" }}
            >
              {t(
                "pickShiftSubtitle",
                "Choose a date and shift above to see a full breakdown of how the shift went across every machine on the line.",
              )}
            </p>
          </motion.div>
        ) : isEmpty ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-16 gap-2 text-center"
          >
            <div className="text-5xl">🗂️</div>
            <h3
              className="text-lg font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              {t("noShiftData", "No data for this shift")}
            </h3>
            <p
              className="text-sm max-w-md"
              style={{ color: "var(--text-secondary)" }}
            >
              {t("noShiftDataSubtitle", "Try a different date or shift.")}
            </p>
          </motion.div>
        ) : hasResult ? (
          <motion.div
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-5"
          >
            <ShiftVerdictHeader
              line={data!.lineSummary}
              timeline={data!.timelineSummary}
              machines={data!.machines}
              culprit={data!.culpritMachines[0]}
              topCause={data!.stopCauses[0]}
              onHover={setHoverSn}
              onSelect={handlePin}
            />

            {/* ── Hero: Line Pulse in the middle ────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
              {/* Left: shift score */}
              <div className="lg:col-span-3">
                <ShiftScoreGauge line={data!.lineSummary} />
              </div>

              {/* Center: Line Pulse — the hero centerpiece */}
              <Panel
                label={t("linePulse", "Line pulse")}
                meta={t("linePulseMeta", "downtime by machine")}
                className="lg:col-span-6"
                bodyClassName="flex items-center"
              >
                <LinePulse
                  machines={data!.machines}
                  culpritMachines={data!.culpritMachines}
                  lineSummary={data!.lineSummary}
                  focusedSn={focusedSn}
                  onHover={setHoverSn}
                  onPin={handlePin}
                />
              </Panel>

              {/* Right: machine list (click/hover to focus everywhere) */}
              <Panel
                label={t("machines", "Machines")}
                right={
                  <span
                    className="text-[11px] tabular-nums"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {data!.machines.length}
                  </span>
                }
                className="lg:col-span-3"
                bodyClassName="overflow-y-auto"
              >
                <div className="flex flex-col gap-1">
                  {data!.machines.map((m) => {
                    const isFocused = focusedSn === m.machine_sn;
                    const dimmed = focusedSn != null && !isFocused;
                    const color = MACHINE_STATUS_COLOR[m.status] ?? "#9ca3af";
                    return (
                      <button
                        key={m.machine_sn}
                        type="button"
                        onClick={() => handlePin(m.machine_sn)}
                        onMouseEnter={() => setHoverSn(m.machine_sn)}
                        onMouseLeave={() => setHoverSn(null)}
                        className="flex items-stretch gap-3 rounded-xl px-2.5 py-2 text-left transition-all"
                        style={{
                          opacity: dimmed ? 0.5 : 1,
                          backgroundColor: isFocused
                            ? "var(--bg-secondary)"
                            : "transparent",
                        }}
                      >
                        <span
                          className="w-1 rounded-full shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <div className="min-w-0 flex-1">
                          <div
                            className="text-[10px] font-semibold uppercase tracking-wider"
                            style={{ color }}
                          >
                            {t(
                              `machineStatus_${m.status}`,
                              MACHINE_STATUS_LABEL[m.status] ?? m.status,
                            )}
                          </div>
                          <div
                            className="text-sm font-semibold truncate"
                            style={{ color: "var(--text-primary)" }}
                            title={m.machine_name}
                          >
                            {m.machine_name}
                          </div>
                          <div
                            className="text-[10px] tabular-nums"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {t("util", "Util")}{" "}
                            {(m.utilization * 100).toFixed(0)}% ·{" "}
                            {fmtDowntime(m.downtime_ms)}
                          </div>
                        </div>
                        <div
                          className="self-center text-sm font-bold tabular-nums shrink-0"
                          style={{ color }}
                        >
                          {(m.efficiency * 100).toFixed(0)}%
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Panel>
            </div>

            {/* KPI curve over the shift (production_period, graceful) */}
            {data!.kpiTimeline.some(
              (p) => p.efficiency != null || p.utilization != null,
            ) && <KpiTimeline points={data!.kpiTimeline} />}

            {/* Downtime + production losses (agg-table panels degrade gracefully) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <DowntimeSplit
                timeline={data!.timelineSummary}
                causes={data!.lostCauses}
                lineDowntimeMs={data!.lineSummary?.total_downtime_ms ?? 0}
              />
              {data!.lostCauses.length > 0 && (
                <LostProduction causes={data!.lostCauses} />
              )}
              {data!.speedLosses.some((l) => l.lost_production > 0) && (
                <SpeedLosses losses={data!.speedLosses} />
              )}
            </div>

            {/* ── Deeper analytics ─────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <MachinePolar
                machines={data!.machines}
                focusedSn={focusedSn}
                onHover={setHoverSn}
                onPin={handlePin}
              />
              <StateBreakdown
                breakdown={data!.stateBreakdown}
                machines={data!.machines}
                focusedSn={focusedSn}
                onHover={setHoverSn}
                onPin={handlePin}
              />
            </div>

            {/* Downtime flow + map (visual insight) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DowntimeSankey
                causes={data!.stopCauses}
                focusedSn={focusedSn}
                onHover={setHoverSn}
                onPin={handlePin}
              />
              <DowntimeTreemap
                causes={data!.stopCauses}
                focusedSn={focusedSn}
                onHover={setHoverSn}
                onPin={handlePin}
              />
            </div>

            {/* Detailed scrubber timeline */}
            <StatusTimeline
              stateLanes={data!.stateLanes}
              machines={data!.machines}
              faultLanes={data!.lanes}
              focusedSn={focusedSn}
              onHover={setHoverSn}
              onPin={handlePin}
            />

            {/* Who blocked whom */}
            <BlameFlow
              blockers={data!.blockers}
              stopCauses={data!.stopCauses}
              machines={data!.machines}
              focusedSn={focusedSn}
              onHover={setHoverSn}
              onPin={handlePin}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ShiftAnalysis;
